import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ErrorModal, usePaginationParams } from '@common-fe/common-fe';
import styled from 'styled-components';

import { OrganizationTypes, SERVER_ERROR_MODAL_TEXT, SERVER_ERROR_MODAL_TITLE } from '@/common/constants';
import Permissions from '@/common/permissions';
import { Access, Topbar } from '@/modules/core/components';
import { useCurrentOrganization, useHistory } from '@/modules/core/hooks';
import { usePlanSectionsScroll } from '@/modules/plan/hooks';
import { PLANS_WRAPPER_UI_ID } from '@/modules/plan/hooks/usePlanSectionsScroll';
import { PlanCardAction, PlanStatuses, PlansType } from '@/modules/plan/plan.types';
import theme from '@/styles/theme';

import { PlanReactivationModal } from '../PlanReactivation';
import { ViewPlanTerminationModal } from '../PlanTermination/ViewPlanTerminationModal';
import { useScrollStore } from '../stores';

import { usePlansListVisibility } from './hooks/usePlansListVisibility';
import CreatePlanModal from './CreatePlanModal';
import CreateTemplateModal from './CreateTemplateModal';
import { usePlansList, useTemplatesList } from './hooks';
import PlansBlock from './PlansBlock';
import PlansRenewalsBanners from './PlansRenewalsBanners';
import PlansToggle from './PlansToggle';
import { useForceOpenModalStore } from './stores';

export const Content = styled.div`
  position: relative;
`;

const FixedWrapper = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 110;
  background-color: ${() => theme.colors.canvas};
`;

export const START_SCROLL_LOCIG_POINT = 100;
export const FINISH_SCROLL_LOCIG_POINT = 40;
export const HEIGHT_FIXED_HEADER = 56;

const PlansList: React.FC = () => {
  const {
    observingOrganization: { type },
  } = useCurrentOrganization();
  const {
    searchString: searchTemplateString,
    setSearchString: setSearchTemplateString,
    currentSearch: currentTemplateSearch,
  } = usePaginationParams();

  const {
    searchString: searchPlanString,
    setSearchString: setSearchPlanString,
    currentSearch: currentPlanSearch,
  } = usePaginationParams();
  const {
    count: plansCount,
    readyForSetupPlans,
    activePlans,
    draftPlans,
    listActivePlans,
    listDraftPlans,
    listReadyForSetupPlans,
    isServerError: isPlansServerError,
    setIsServerError: setIsPlansServerError,
    currentRefetch: currentRefetchPlans,
    isDraftPlansLoading,
    isActivePlansLoading,
    isReadyForSetupPlansLoading,
    listTerminatedPlans,
    isTerminatedPlansLoading,
    terminatedPlans,
    refetchTerminatedPlans,
  } = usePlansList(currentPlanSearch, type);

  const { handleReset } = useScrollStore();

  const { searchQuery, hash } = useHistory();

  const { showPlansTab, showTemplatesTab, showContent, isSubsidiary } = usePlansListVisibility();

  const isEmployer = useMemo(
    () => type === OrganizationTypes.employer || type === OrganizationTypes.company,
    [type]
  );

  const [isScrolled, setIsScrolled] = useState(false);

  const planRedirectPath = useMemo(() => {
    if (!showPlansTab && showTemplatesTab) {
      return PlansType.Template;
    }
    if (showPlansTab && !showTemplatesTab) {
      return PlansType.Plan;
    }
    return (hash as PlansType) || PlansType.Plan;
  }, [hash, showPlansTab, showTemplatesTab]);

  const [currentTab, setCurrentTab] = useState(planRedirectPath);
  useEffect(() => {
    if (!showPlansTab && currentTab === PlansType.Plan && showTemplatesTab) {
      setCurrentTab(PlansType.Template);
    }
    if (!showTemplatesTab && currentTab === PlansType.Template && showPlansTab) {
      setCurrentTab(PlansType.Plan);
    }
  }, [currentTab, showPlansTab, showTemplatesTab]);
  useEffect(() => {
    setCurrentTab(planRedirectPath);
  }, [planRedirectPath]);
  useEffect(() => {
    const appContainer = document.getElementById(PLANS_WRAPPER_UI_ID);
    const updateScrolling = () => {
      if (appContainer && appContainer.scrollTop >= START_SCROLL_LOCIG_POINT) setIsScrolled(true);
      if (appContainer && appContainer.scrollTop < 40) setIsScrolled(false);
    };

    appContainer?.addEventListener('scroll', updateScrolling);

    return () => {
      setIsScrolled(false);
      handleReset();
      appContainer?.removeEventListener('scroll', updateScrolling);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  const {
    count: templatesCount,
    inactiveTemplates,
    activeTemplates,
    allDraftTemplates,
    listActiveTemplates,
    allListDraftTemplates,
    listInactiveTemplates,
    isActiveTemplatesLoading,
    isDraftTemplatesLoading,
    isInactiveTemplatesLoading,
  } = useTemplatesList(currentTemplateSearch, type);

  const { scrollToSection, onScrollHandler } = usePlanSectionsScroll(
    currentTab === PlansType.Template
      ? [PlanStatuses.DRAFT, PlanStatuses.ACTIVE, PlanStatuses.INACTIVE]
      : [PlanStatuses.READY_FOR_SETUP, PlanStatuses.DRAFT, PlanStatuses.ACTIVE]
  );

  const handleSetModalVisible = useForceOpenModalStore((state) => state.setState);
  useEffect(
    () => () => {
      handleReset();
    },
    [handleReset]
  );
  const actionTypeState = useForceOpenModalStore((state) => state.state.actionType);
  useEffect(() => {
    if (actionTypeState) {
      if (actionTypeState === PlanCardAction.createPlanFromTemplate && currentTab !== PlansType.Plan) {
        setCurrentTab(PlansType.Plan);
      } else if (
        actionTypeState === PlanCardAction.createTemplateFromTemplate &&
        currentTab !== PlansType.Template
      ) {
        setCurrentTab(PlansType.Template);
      }
    }
  }, [actionTypeState, currentTab]);

  const handleSelectAction = useCallback(
    (value: PlanCardAction, id: string) => {
      handleSetModalVisible(value, id);
    },
    [handleSetModalVisible]
  );

  return (
    <Box
      width="100%"
      overflow="auto"
      onScroll={onScrollHandler}
      id={PLANS_WRAPPER_UI_ID}
      pad={{ bottom: 'xxxl' }}
    >
      <Box flex="grow">
        <Topbar />
        <FixedWrapper>
          <Box
            width="100%"
            alignSelf="center"
            align="center"
            pad={{ horizontal: '40px' }}
            margin={{ vertical: isScrolled ? '0' : 'l' }}
            border={isScrolled && { size: 'small', color: 'border1', side: 'bottom' }}
          >
            <PlansToggle
              link={searchQuery}
              currentTab={currentTab}
              onSetCurrentTab={setCurrentTab}
              setSearchString={currentTab === PlansType.Plan ? setSearchPlanString : setSearchTemplateString}
              searchString={currentTab === PlansType.Plan ? searchPlanString : searchTemplateString}
              clearSearchValue={() => {
                setSearchPlanString('');
                setSearchTemplateString('');
              }}
              plansCount={plansCount}
              hidePlans={!showPlansTab}
              hideTemplates={!showTemplatesTab}
              hideAll={!showContent}
              templatesCount={templatesCount}
              isScrolled={isScrolled}
              isEmployer={isEmployer}
            >
              {/* {(observingEmployer?.id || observingPartner?.id) && ( */}
              {!isSubsidiary && showContent && (
                <>
                  <Access
                    accessRequirements={[
                      {
                        permission: Permissions.PLAN_SETUP,
                      },
                    ]}
                  >
                    <CreatePlanModal hide={currentTab !== PlansType.Plan} />
                  </Access>
                  <Access
                    accessRequirements={[
                      {
                        permission: Permissions.PLAN_TEMPLATE_SETUP,
                      },
                    ]}
                  >
                    <CreateTemplateModal hide={currentTab !== PlansType.Template} />
                  </Access>
                </>
              )}
            </PlansToggle>
          </Box>
        </FixedWrapper>
        <Box>
          {!showContent ? null : (
            <Box data-testid="plans-list" style={{ position: 'relative' }}>
              <Box width={theme.defaultContentWidth} alignSelf="center">
                <PlansRenewalsBanners />
                <Content>
                  {currentTab === PlansType.Template ? (
                    <>
                      {!isSubsidiary && (
                        <PlansBlock
                          isTemplateMode
                          title="Draft"
                          plans={allDraftTemplates}
                          plansList={allListDraftTemplates}
                          status={PlanStatuses.DRAFT}
                          placeholder={
                            searchTemplateString
                              ? 'No results have been found in Draft Templates.'
                              : 'No draft templates. Create a new plan template.'
                          }
                          isRestoreButtonShow={!!searchTemplateString && !allDraftTemplates.length}
                          scrollToSection={scrollToSection}
                          isLoading={isDraftTemplatesLoading}
                          clearSearchValue={() => setSearchTemplateString('')}
                          searchValue={searchTemplateString}
                          currentTab={currentTab}
                          isScrolled={isScrolled}
                        />
                      )}
                      <PlansBlock
                        isTemplateMode
                        title="Active"
                        onSelectAction={handleSelectAction}
                        plans={activeTemplates}
                        plansList={listActiveTemplates}
                        status={PlanStatuses.ACTIVE}
                        isPlansHidden={!showPlansTab}
                        placeholder={
                          searchTemplateString
                            ? 'No results have been found in Active Templates.'
                            : 'No Active Templates. Activate a plan template.'
                        }
                        isRestoreButtonShow={!!searchTemplateString && !activeTemplates.length}
                        scrollToSection={scrollToSection}
                        isLoading={isActiveTemplatesLoading}
                        clearSearchValue={() => setSearchTemplateString('')}
                        searchValue={searchTemplateString}
                        currentTab={currentTab}
                        isScrolled={isScrolled}
                      />
                      <PlansBlock
                        isTemplateMode
                        title="Inactive"
                        plans={inactiveTemplates}
                        plansList={listInactiveTemplates}
                        status={PlanStatuses.INACTIVE}
                        placeholder={
                          searchTemplateString
                            ? 'No results have been found in Inactive Templates.'
                            : 'No inactive templates.'
                        }
                        isRestoreButtonShow={!!searchTemplateString && !inactiveTemplates.length}
                        scrollToSection={scrollToSection}
                        isLoading={isInactiveTemplatesLoading}
                        clearSearchValue={() => setSearchTemplateString('')}
                        searchValue={searchTemplateString}
                        currentTab={currentTab}
                        isScrolled={isScrolled}
                      />
                    </>
                  ) : (
                    <>
                      {!isSubsidiary && (
                        <PlansBlock
                          title="Ready for Setup"
                          plans={readyForSetupPlans}
                          plansList={listReadyForSetupPlans}
                          status={PlanStatuses.READY_FOR_SETUP}
                          placeholder={
                            searchPlanString
                              ? 'No results have been found in Ready for Setup Plans.'
                              : 'No plans ready for setup. Create a new plan.'
                          }
                          isRestoreButtonShow={!!searchPlanString && !readyForSetupPlans.length}
                          scrollToSection={scrollToSection}
                          isLoading={isReadyForSetupPlansLoading}
                          clearSearchValue={() => setSearchPlanString('')}
                          searchValue={searchPlanString}
                          currentTab={currentTab}
                          isScrolled={isScrolled}
                        />
                      )}
                      {!isSubsidiary && (
                        <PlansBlock
                          title="Drafts"
                          plans={draftPlans}
                          plansList={listDraftPlans}
                          status={PlanStatuses.DRAFT}
                          placeholder={
                            searchPlanString
                              ? 'No results have been found in Draft Plans.'
                              : 'No drafts. Save a draft.'
                          }
                          isRestoreButtonShow={!!searchPlanString && !draftPlans.length}
                          scrollToSection={scrollToSection}
                          isLoading={isDraftPlansLoading}
                          clearSearchValue={() => setSearchPlanString('')}
                          searchValue={searchPlanString}
                          currentTab={currentTab}
                          isScrolled={isScrolled}
                        />
                      )}
                      <PlansBlock
                        title="Active Plans"
                        plans={activePlans}
                        plansList={listActivePlans}
                        status={PlanStatuses.ACTIVE}
                        placeholder={
                          searchPlanString
                            ? 'No results have been found in Active Plans.'
                            : 'No active plans. Activate a plan.'
                        }
                        isRestoreButtonShow={!!searchPlanString && !activePlans.length}
                        scrollToSection={scrollToSection}
                        isLoading={isActivePlansLoading}
                        clearSearchValue={() => setSearchPlanString('')}
                        searchValue={searchPlanString}
                        currentTab={currentTab}
                        isScrolled={isScrolled}
                      />
                      <PlansBlock
                        title="Terminated"
                        plans={terminatedPlans}
                        plansList={listTerminatedPlans}
                        status={PlanStatuses.TERMINATED}
                        placeholder={
                          searchPlanString
                            ? 'No results have been found in Terminated Plans.'
                            : 'No terminated plans.'
                        }
                        isRestoreButtonShow={!!searchPlanString && !activePlans.length}
                        scrollToSection={scrollToSection}
                        isLoading={isTerminatedPlansLoading}
                        clearSearchValue={() => setSearchPlanString('')}
                        searchValue={searchPlanString}
                        currentTab={currentTab}
                        isScrolled={isScrolled}
                        onRefetch={refetchTerminatedPlans}
                      />
                    </>
                  )}
                </Content>
              </Box>
            </Box>
          )}
          <ErrorModal
            visible={isPlansServerError}
            header={SERVER_ERROR_MODAL_TITLE}
            helpText={SERVER_ERROR_MODAL_TEXT}
            buttonText="Close"
            buttonTextTryAgain="Try again"
            onSetVisible={setIsPlansServerError}
            onSubmit={currentRefetchPlans}
          />
          <ViewPlanTerminationModal />
          <PlanReactivationModal />
        </Box>
      </Box>
    </Box>
  );
};

export default PlansList;
